import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useRef, useState } from "react"
import { FiEdit2, FiList, FiPlay, FiPlusCircle, FiTrash2 } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import {
  type CreateInventoryCountPlanDTO,
  InventoryCountApi,
  type InventoryCountPlanDTO,
  type RunDuePlansResultDTO,
  type UpdateInventoryCountPlanDTO,
} from "../../../../api/inventoryCount"
import { WareHouseService } from "../../../../client"
import { WarehouseLocationPicker } from "../../../../components/WMS/shared/WarehouseLocationPicker"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"

export const Route = createFileRoute("/_layout/WMS/inventory-count/plans")({
  component: InventoryCountPlansPage,
})

type GenerationSummary = {
  status: "generated" | "failed" | "skipped"
  message?: string
  taskNo?: string
  updatedAt: string
}

function InventoryCountPlansPage() {
  const qc = useQueryClient()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [warehouseId, setWarehouseId] = useState("")
  const [status, setStatus] = useState("")
  const [runDueLimit, setRunDueLimit] = useState("25")
  const [createError, setCreateError] = useState("")
  const [opsError, setOpsError] = useState("")
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null)
  const [planToDelete, setPlanToDelete] =
    useState<InventoryCountPlanDTO | null>(null)
  const [lastRunDueResult, setLastRunDueResult] =
    useState<RunDuePlansResultDTO | null>(null)
  const [generationByPlan, setGenerationByPlan] = useState<
    Record<string, GenerationSummary>
  >({})
  const [form, setForm] = useState<CreateInventoryCountPlanDTO>({
    warehouse_id: "",
    name: "",
    strategy: "manual",
    frequency: "weekly",
    status: "active",
    zone_filter: "",
    location_filter: "",
  })
  const cancelDeleteRef = useRef<HTMLButtonElement | null>(null)

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list", { includeArchived: false }],
    queryFn: () => WareHouseService.getAllWarehouses(false),
    retry: false,
  })
  const selectedWarehouse = useMemo(
    () =>
      (warehousesQuery.data ?? []).find(
        (warehouse) => warehouse.id === form.warehouse_id,
      ) ?? null,
    [form.warehouse_id, warehousesQuery.data],
  )

  const plansQuery = useQuery({
    queryKey: ["wms", "inventory-count", "plans", { warehouseId, status }],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getPlans({
        warehouse_id: warehouseId || undefined,
        status: status || undefined,
        page: 1,
        page_size: 100,
      }),
  })

  const createPlanMutation = useMutation({
    mutationFn: InventoryCountApi.createPlan,
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
      setForm({
        warehouse_id: "",
        name: "",
        strategy: "manual",
        frequency: "weekly",
        status: "active",
        zone_filter: "",
        location_filter: "",
      })
      setCreateError("")
      onClose()
    },
    onError: (error: unknown) => {
      setCreateError(
        parseApiError(error, "Nie udało się utworzyć planu inwentaryzacji."),
      )
    },
  })

  const updatePlanMutation = useMutation({
    mutationFn: ({
      planId,
      payload,
    }: {
      planId: string
      payload: UpdateInventoryCountPlanDTO
    }) => InventoryCountApi.updatePlan(planId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
      resetFormState()
      setCreateError("")
      onClose()
    },
    onError: (error: unknown) => {
      setCreateError(
        parseApiError(
          error,
          "Nie udało się zaktualizować planu inwentaryzacji.",
        ),
      )
    },
  })

  const deletePlanMutation = useMutation({
    mutationFn: (planId: string) => InventoryCountApi.deletePlan(planId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
      setPlanToDelete(null)
      setOpsError("")
    },
    onError: (error: unknown) => {
      setOpsError(
        parseApiError(error, "Nie udało się usunąć planu inwentaryzacji."),
      )
    },
  })

  const generateTasksMutation = useMutation({
    mutationFn: (planId: string) =>
      InventoryCountApi.generateTasksFromPlan(planId, {}),
    onSuccess: async (result, planId) => {
      setOpsError("")
      setGenerationByPlan((prev) => ({
        ...prev,
        [planId]: {
          status: "generated",
          taskNo: result.task_no,
          message: `lines: ${result.lines_count}`,
          updatedAt: new Date().toISOString(),
        },
      }))
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
    },
    onError: (error, planId) => {
      const message =
        getApiStatus(error) === 409
          ? "Plan ma już aktywne zadanie. Lista została odświeżona."
          : parseApiError(error, "Nie udało się wygenerować taska.")
      setOpsError(message)
      setGenerationByPlan((prev) => ({
        ...prev,
        [planId]: {
          status: "failed",
          message,
          updatedAt: new Date().toISOString(),
        },
      }))
      void qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
    },
  })

  const runDueMutation = useMutation({
    mutationFn: (limit: number) => InventoryCountApi.runDuePlans(limit),
    onSuccess: async (result) => {
      setOpsError("")
      setLastRunDueResult(result)
      setGenerationByPlan((prev) => {
        const next = { ...prev }
        result.items.forEach((item) => {
          next[item.plan_id] = {
            status:
              item.status === "generated"
                ? "generated"
                : item.status === "skipped"
                  ? "skipped"
                  : "failed",
            taskNo: item.task_no,
            message: item.message,
            updatedAt: result.processed_at,
          }
        })
        return next
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "plans"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
    },
    onError: (error) => {
      setOpsError(parseApiError(error, "Nie udało się uruchomić run-due."))
    },
  })

  const plans = plansQuery.data?.items ?? []
  const runDueSummary = useMemo(() => {
    if (!lastRunDueResult) {
      return null
    }
    return `Przetworzone: ${lastRunDueResult.processed}, wygenerowane: ${lastRunDueResult.generated}, błędne: ${lastRunDueResult.failed}`
  }, [lastRunDueResult])

  const resetFormState = () => {
    setEditingPlanId(null)
    setForm({
      warehouse_id: "",
      name: "",
      strategy: "manual",
      frequency: "weekly",
      status: "active",
      zone_filter: "",
      location_filter: "",
    })
  }

  const closePlanModal = () => {
    resetFormState()
    setCreateError("")
    onClose()
  }

  const openCreateModal = () => {
    resetFormState()
    setCreateError("")
    onOpen()
  }

  const openEditModal = (plan: InventoryCountPlanDTO) => {
    setEditingPlanId(plan.id)
    setForm({
      warehouse_id: plan.warehouse_id,
      name: plan.name,
      strategy: plan.strategy,
      frequency: plan.frequency,
      status: plan.status,
      zone_filter: plan.zone_filter ?? "",
      location_filter: plan.location_filter ?? "",
    })
    setCreateError("")
    onOpen()
  }

  const submitPlanForm = () => {
    if (editingPlanId) {
      updatePlanMutation.mutate({
        planId: editingPlanId,
        payload: {
          warehouse_id: form.warehouse_id,
          name: form.name,
          strategy: form.strategy,
          frequency: form.frequency,
          status: form.status,
          zone_filter: form.zone_filter || undefined,
          location_filter: form.location_filter || undefined,
        },
      })
      return
    }
    createPlanMutation.mutate(form)
  }

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Plany inwentaryzacji"
        description="Zarządzanie planami inwentaryzacji, ręcznym generowaniem i uruchamianiem zadań zaplanowanych."
        gradient="linear(to-r, blue.600, cyan.600)"
        badge={`Plany: ${plansQuery.data?.total ?? 0}`}
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <WmsListToolbar
          actionsWrap="nowrap"
          filters={
            <>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "260px" }}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie magazyny</option>
                {(warehousesQuery.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} - {warehouse.name}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "220px" }}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie statusy</option>
                <option value="active">aktywny</option>
                <option value="paused">wstrzymany</option>
                <option value="archived">zarchiwizowany</option>
              </Select>
            </>
          }
          actions={
            <>
              <Select
                size="sm"
                w="160px"
                value={runDueLimit}
                onChange={(event) => setRunDueLimit(event.target.value)}
                borderRadius="xl"
              >
                <option value="10">zaplanowane: 10</option>
                <option value="25">zaplanowane: 25</option>
                <option value="50">zaplanowane: 50</option>
              </Select>
              <Stack spacing={1} align="center">
                <Tooltip label="Uruchom pozycji." hasArrow placement="top">
                  <IconButton
                    size="sm"
                    aria-label="Uruchom zaplanowane teraz"
                    colorScheme="teal"
                    variant="outline"
                    borderRadius="xl"
                    icon={<FiPlay />}
                    isLoading={runDueMutation.isPending}
                    onClick={() =>
                      runDueMutation.mutate(Number(runDueLimit) || 25)
                    }
                  />
                </Tooltip>
              </Stack>
              <Stack spacing={1} align="center">
                <Tooltip label="Utwórz nowy plan." hasArrow placement="top">
                  <IconButton
                    size="sm"
                    aria-label="Nowy plan"
                    colorScheme="blue"
                    borderRadius="xl"
                    icon={<FiPlusCircle />}
                    onClick={openCreateModal}
                  />
                </Tooltip>
              </Stack>
            </>
          }
        />

        {opsError ? (
          <Text color="red.500" fontSize="sm" mb={2}>
            {opsError}
          </Text>
        ) : null}

        {runDueSummary ? (
          <Box borderWidth="1px" borderRadius="xl" p={3} mb={3}>
            <Text fontSize="sm" fontWeight="semibold">
              Wynik ostatniego uruchomienia
            </Text>
            <Text fontSize="sm" color={mutedText}>
              {new Date(lastRunDueResult!.processed_at).toLocaleString()} |{" "}
              {runDueSummary}
            </Text>
          </Box>
        ) : null}

        <Box flex="1" minH={0}>
          <WmsTableListCard
            isLoading={plansQuery.isPending}
            isFetching={plansQuery.isFetching}
            error={plansQuery.isError ? plansQuery.error : undefined}
            errorMessage="Nie udało się pobrać planów inwentaryzacji."
            isEmpty={
              !plansQuery.isPending && !plansQuery.isError && plans.length === 0
            }
            emptyMessage="Brak planów inwentaryzacji dla wybranych filtrów."
            minEmptyHeight={{ base: "200px", lg: "320px" }}
          >
            <TableContainer maxH="62vh" overflowY="auto" overflowX="hidden">
              <Table size="sm" sx={{ tableLayout: "fixed", width: "100%" }}>
                <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th w="190px">Nazwa</Th>
                    <Th w="105px">Strategia</Th>
                    <Th w="105px">Częstotliwość</Th>
                    <Th w="92px">Status</Th>
                    <Th w="150px">Zakres lokacji</Th>
                    <Th w="140px">Następne uruch.</Th>
                    <Th w="140px">Ostatnie uruch.</Th>
                    <Th w="170px">Wynik</Th>
                    <Th w="180px">Akcje</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {plans.map((plan, idx) => {
                    const summary = generationByPlan[plan.id]
                    const activeTask = plan.active_task
                    return (
                      <Tr
                        key={plan.id}
                        bg={idx % 2 === 0 ? "transparent" : zebraBg}
                      >
                        <Td>{plan.name}</Td>
                        <Td>{plan.strategy}</Td>
                        <Td>{plan.frequency}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              plan.status === "active" ? "green" : "gray"
                            }
                          >
                            {plan.status}
                          </Badge>
                        </Td>
                        <Td>
                          <Stack spacing={0}>
                            <Text fontSize="sm">
                              Od: {plan.zone_filter?.trim() || "—"}
                            </Text>
                            <Text fontSize="xs" color={mutedText}>
                              Do: {plan.location_filter?.trim() || "—"}
                            </Text>
                          </Stack>
                        </Td>
                        <Td>
                          {plan.next_run_at ? (
                            new Date(plan.next_run_at).toLocaleString()
                          ) : (
                            <Text color={mutedText}>—</Text>
                          )}
                        </Td>
                        <Td>
                          {plan.last_run_at ? (
                            new Date(plan.last_run_at).toLocaleString()
                          ) : (
                            <Text color={mutedText}>—</Text>
                          )}
                        </Td>
                        <Td>
                          {activeTask ? (
                            <Stack spacing={0}>
                              <Badge colorScheme="orange" width="fit-content">
                                aktywne zadanie
                              </Badge>
                              <Link
                                to="/WMS/inventory-count/tasks/$taskId"
                                params={{ taskId: activeTask.id }}
                              >
                                <Text fontSize="xs" color="blue.600">
                                  {activeTask.task_no} ({activeTask.status})
                                </Text>
                              </Link>
                            </Stack>
                          ) : summary ? (
                            <Stack spacing={0}>
                              <Badge
                                colorScheme={
                                  summary.status === "generated"
                                    ? "green"
                                    : summary.status === "skipped"
                                      ? "orange"
                                      : "red"
                                }
                                width="fit-content"
                              >
                                {summary.status}
                              </Badge>
                              <Text fontSize="xs" color={mutedText}>
                                {summary.taskNo
                                  ? `zadanie: ${summary.taskNo}`
                                  : summary.message || "—"}
                              </Text>
                              <Text fontSize="xs" color={mutedText}>
                                {new Date(summary.updatedAt).toLocaleString()}
                              </Text>
                            </Stack>
                          ) : (
                            <Text color={mutedText}>—</Text>
                          )}
                        </Td>
                        <Td>
                          <HStack gap={2} align="center" flexWrap="nowrap">
                            <Stack spacing={0} align="center">
                              <Tooltip
                                label="Wygeneruj zadania."
                                hasArrow
                                placement="top"
                              >
                                <IconButton
                                  aria-label="Generuj zadania"
                                  size="sm"
                                  colorScheme="blue"
                                  icon={<FiPlay />}
                                  isDisabled={Boolean(activeTask)}
                                  isLoading={
                                    generateTasksMutation.isPending &&
                                    generateTasksMutation.variables === plan.id
                                  }
                                  onClick={() => {
                                    if (!activeTask) {
                                      generateTasksMutation.mutate(plan.id)
                                    }
                                  }}
                                />
                              </Tooltip>
                            </Stack>
                            <Stack spacing={0} align="center">
                              <Tooltip
                                label="Edytuj plan."
                                hasArrow
                                placement="top"
                              >
                                <IconButton
                                  aria-label="Edytuj plan"
                                  size="sm"
                                  variant="outline"
                                  icon={<FiEdit2 />}
                                  onClick={() => openEditModal(plan)}
                                />
                              </Tooltip>
                            </Stack>
                            <Stack spacing={0} align="center">
                              <Tooltip
                                label="Usuń plan."
                                hasArrow
                                placement="top"
                              >
                                <IconButton
                                  aria-label="Usuń plan"
                                  size="sm"
                                  variant="outline"
                                  colorScheme="red"
                                  icon={<FiTrash2 />}
                                  onClick={() => setPlanToDelete(plan)}
                                />
                              </Tooltip>
                            </Stack>
                            <Stack spacing={0} align="center">
                              <Tooltip
                                label="Przejdź do listy zadań."
                                hasArrow
                                placement="top"
                              >
                                <IconButton
                                  as={Link}
                                  to="/WMS/inventory-count/tasks"
                                  aria-label="Otwórz zadania"
                                  size="sm"
                                  variant="outline"
                                  icon={<FiList />}
                                />
                              </Tooltip>
                            </Stack>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </WmsTableListCard>
        </Box>
      </WmsPagePanel>

      <Modal isOpen={isOpen} onClose={closePlanModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPlanId
              ? "Edytuj plan inwentaryzacji"
              : "Nowy plan inwentaryzacji"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              <FormControl>
                <FormLabel mb={1}>Magazyn</FormLabel>
                <Select
                  size="sm"
                  value={form.warehouse_id}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      warehouse_id: event.target.value,
                      zone_filter: "",
                      location_filter: "",
                    }))
                  }
                >
                  <option value="">Wybierz magazyn</option>
                  {(warehousesQuery.data ?? []).map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} - {warehouse.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel mb={1}>Nazwa planu</FormLabel>
                <Input
                  size="sm"
                  placeholder="Nazwa planu"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel mb={1}>Strategia</FormLabel>
                <Select
                  size="sm"
                  value={form.strategy}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      strategy: event.target.value,
                    }))
                  }
                >
                  <option value="manual">ręczny</option>
                  <option value="abc">abc</option>
                  <option value="threshold">threshold</option>
                  <option value="risk">risk</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel mb={1}>Częstotliwość</FormLabel>
                <Select
                  size="sm"
                  value={form.frequency}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      frequency: event.target.value,
                    }))
                  }
                >
                  <option value="daily">codziennie</option>
                  <option value="weekly">co tydzień</option>
                  <option value="monthly">co miesiąc</option>
                  <option value="manual">ręcznie</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel mb={1}>Status</FormLabel>
                <Select
                  size="sm"
                  value={form.status ?? "active"}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="active">aktywny</option>
                  <option value="paused">wstrzymany</option>
                  <option value="archived">zarchiwizowany</option>
                </Select>
              </FormControl>
              <WarehouseLocationPicker
                value={form.zone_filter ?? ""}
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    zone_filter: value,
                  }))
                }
                template={selectedWarehouse?.location_template ?? null}
                title="Wybierz lokację startową"
                placeholder="Lokacja startowa"
              />
              <WarehouseLocationPicker
                value={form.location_filter ?? ""}
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    location_filter: value,
                  }))
                }
                template={selectedWarehouse?.location_template ?? null}
                title="Wybierz lokację końcową"
                placeholder="Lokacja końcowa"
              />
              <Text fontSize="sm" color={mutedText}>
                Możesz uzupełnić tylko lokację startową, tylko końcową albo oba
                pola. Puste pole oznacza brak ograniczenia z tej strony.
              </Text>
              {createError ? (
                <Text color="red.500" fontSize="sm">
                  {createError}
                </Text>
              ) : null}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" mr={3} onClick={closePlanModal} variant="ghost">
              Anuluj
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={
                createPlanMutation.isPending || updatePlanMutation.isPending
              }
              onClick={submitPlanForm}
            >
              {editingPlanId ? "Zapisz zmiany" : "Utwórz plan"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={!!planToDelete}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setPlanToDelete(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Usunąć plan inwentaryzacji?
            </AlertDialogHeader>
            <AlertDialogBody>
              {planToDelete
                ? `Plan "${planToDelete.name}" zostanie usunięty. Wygenerowane wcześniej zadania pozostaną w historii bez przypisanego planu.`
                : "Plan zostanie usunięty."}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                size="sm"
                ref={cancelDeleteRef}
                onClick={() => setPlanToDelete(null)}
                variant="ghost"
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                ml={3}
                isLoading={deletePlanMutation.isPending}
                onClick={() => {
                  if (!planToDelete) return
                  deletePlanMutation.mutate(planToDelete.id)
                }}
              >
                Usuń plan
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </WmsPageShell>
  )
}

function parseApiError(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "body" in error &&
    typeof (error as { body?: { error?: string } }).body?.error === "string"
  ) {
    return (error as { body?: { error?: string } }).body?.error ?? fallback
  }
  return fallback
}

function getApiStatus(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null && "status" in error) {
    const status = (error as { status?: unknown }).status
    return typeof status === "number" ? status : undefined
  }
  return undefined
}
